import {L} from "../lt-ng/landcare-toolkit";
import {LTServices} from "../lt-ng/lt-services";
import {CustomLayerAPI} from "./custom-layer-api";
import {ILoginService} from "./oe/login-service";
import {_F} from "../utility/func";
import {FeatureLayer, GroupLayer, ImageService, Layers, Legend} from "esri/Features";
import {MapService} from "../@types/esri/MapService";
import {EsriJSON, WMTSCapabilities} from "ol/format";
import VectorSource from "ol/source/Vector";
import {transformExtent} from "ol/proj";
import {tile} from "ol/loadingstrategy";
import {createXYZ} from "ol/tilegrid";
import VectorLayer from "ol/layer/Vector";
import {Extent} from "ol/extent";
import {TileArcGISRest, TileImage} from "ol/source";
import TileGrid from "ol/tilegrid/TileGrid";
import TileLayer from "ol/layer/Tile";
import {Coordinate} from "ol/coordinate";
import {Size} from "ol/size";
import {Feature as OlFeature} from 'ol';
import {getProjection} from '../utility/WMTS'
import {FeatureLoader} from "ol/featureloader";
import FeatureFormat from "ol/format/Feature";
import {customLayerStyleFn} from "./features-api/features-layer";
import { createStyleFunctionFromUrl, setMapProjection } from 'ol-esri-style';
import {Geometry} from "ol/geom";
// import {supportsGetFeatureInfo} from "../lt-ng/wmts-getfeatureinfo-interaction";

/**
 * Takes care of the creation of custom layers.
 */
export module CustomLayers {
	import Item = L.tree.Item;
	import IMapService = LTServices.IMapService;
	import Layer = L.util.Layer;
	import LayerService = L.util.LayerService;
	import CustomLayer = CustomLayerAPI.CustomLayer;
	import CustomLayerType = CustomLayerAPI.CustomLayerType;
	import CustomWMTSLayer = CustomLayerAPI.CustomWMTSLayer;
	import requestCustomLayers = CustomLayerAPI.requestCustomLayers;
	import saveCustomLayer = CustomLayerAPI.saveCustomLayer;
	import deleteCustomLayer = CustomLayerAPI.deleteCustomLayer;
	import uniqueBy = _F.uniqueBy;
	import CustomEsriRestAPILayer = CustomLayerAPI.CustomEsriRestAPILayer;
	import esriServiceName = CustomLayerAPI.esriServiceName;
	import filterNodes = L.tree.filterNodes;
	import projFromSpatialReference = CustomLayerAPI.projFromSpatialReference;

	export interface ICustomLayerService {
		createCustomLayer(customLayerSpec: CustomLayer) : ng.IPromise<Item>;
		saveNode(node: Item);
		save();
		load();
		specForNode(node: Item): CustomLayer;
		removeLayer(item: Item);
	}

	export class CustomLayerService implements ICustomLayerService {
		public static $inject = [
			'$http',
			'landcareMapService',
			'$q',
			'$rootScope',
			'loginService'];

		protected customLayers: CustomLayer[];

		constructor(
			protected $http: ng.IHttpService,
			protected mapService: IMapService,
			protected $q: ng.IQService,
			protected $rootScope: ng.IRootScopeService,
			protected loginService: ILoginService) {
			this.customLayers = [];

			this.load();

			$rootScope.$on('saveLayer', (ev, item : Item) => {
				if (!item) return;

				let spec = this.specLookup[item.id];
				if (spec) {
					CustomLayers.CustomLayerService.updateSpec(item, spec, item.parent.children.indexOf(item));
					saveCustomLayer($http, spec);
				}
			});

			$rootScope.$on('layerToggled', (ev, item: Item) => {
				if (!item.isCustom) return;

				let spec : CustomLayer = this.specLookup[item.id];

				if (!spec) return;

				CustomLayers.CustomLayerService.updateSpec(item, spec, item.parent.children.indexOf(item));
				saveCustomLayer($http, spec);
			});

			this.$rootScope.$on('loginService:success', (ev, currentMember, previousMember) => {
				if (currentMember != previousMember) {
					let grp = this.mapService.getCustomLayerGroup();
					if (grp) {
                        grp.children.splice(0, grp.children.length);
						grp.layerSelectorChildren.splice(0, grp.children.length);
                        this.$rootScope.$broadcast('updateTreeData');
                        this.$rootScope.$broadcast('onTreeChange');
					}

					this.loaded = false;
					this.load();
				}
			})
		}

		private static updateSpec(node: Item, spec: CustomLayer, index: number) {
			if (!spec || !node) return;
			spec.visible= node.checked;
			spec.included = node.extraState.metaLayerSelector.checked;
			spec.minScale = node.layerService.minScale;
			spec.maxScale = node.layerService.maxScale;
			spec.layerType = node.layerType;
			spec.order = index;
		}

		removeLayer(item: Item) : boolean {
			let id = item.id;
			if (!(id in this.specLookup)) {
				console.log(`Custom layer ${item.name} not found, will not be removed`);
				return false;
			}

			let spec = this.specLookup[id];
			let idx = this.customLayers.indexOf(spec);
			if (idx !== -1) {
				this.customLayers.splice(idx, 1);
			}

			deleteCustomLayer(this.$http, spec);
			return true;
		}

		layerSpecToNode(customLayerSpec: CustomLayer) : ng.IPromise<Item> {
			switch (customLayerSpec.kind) {
				case CustomLayerType.WMTS:
					return this.customWMTSNode(customLayerSpec);
				case CustomLayerAPI.CustomLayerType.EsriImage:
					return this.customEsriImageNode(customLayerSpec)
						.then(item => {
							filterNodes(item, f => f.layer)
								.forEach(node => {
									node.layerType = customLayerSpec.layerType;
									node.layer.setZIndex(Layer.zIndexForLayerType(customLayerSpec.layerType, customLayerSpec.order + 1));
									node.layer.set('transparencyLayer', customLayerSpec.layerType == 'SCIENCE');
								});

							return item;
						})
			}

			return this.$q.reject('Unsupported kind of CustomLayer');
		}

		createCustomLayer(customLayerSpec: CustomLayer): ng.IPromise<Item> {

			let result = this.layerSpecToNode(customLayerSpec);

			result.then((node: Item) => {
					if (!node) return;

					this.$rootScope.$broadcast('updateTreeData');
					this.$rootScope.$broadcast('onTreeChange');

					if (customLayerSpec.order === undefined) {
						customLayerSpec.order = this.customLayers.length;
					}
					this.customLayers.push(customLayerSpec);
					this.customLayers.sort((a, b) => a.order - b.order);
					// this.save();

					CustomLayers.CustomLayerService.updateSpec(node, customLayerSpec, customLayerSpec.order);
					saveCustomLayer(this.$http, customLayerSpec);
			});

			return result;
		}

		specForNode(node: Item): CustomLayer {
			return this.specLookup[node.id];
		}

		saveNode(node: Item) {
			const spec = this.specLookup[node.id] as CustomLayer;
			CustomLayerService.updateSpec(node, spec, spec.order);
			saveCustomLayer(this.$http, spec);
		}

		save() {
			let customLayerGroup = this.mapService.getCustomLayerGroup();

			this.customLayers = customLayerGroup.children.map((node, i) => {
				filterNodes(node, (item: Item) => !!item.layer)
					.forEach((item: Item) => {
						item.layer.setZIndex(Layer.zIndexForLayerType(item.layerType, i + 1))
					});

				let spec = <CustomLayer> this.specLookup[node.id];
				CustomLayers.CustomLayerService.updateSpec(node, spec, i);
				return spec;
			});

			this.customLayers.forEach((cl) => saveCustomLayer(this.$http, cl));
		}

		private loaded: boolean = false;
		load() {
			if (!this.loaded) {
				if (!this.loginService.currentMemberId()) {
					return;
				}

				requestCustomLayers(this.$http).then((response) => {
					this.$q.all([this.mapService.mapPromise, this.mapService.promise]).then(() => {
					// this.mapService.mapPromise.then(() => {
						uniqueBy(response.data, (f: CustomLayer) => {
							switch (f.kind) {
								/*case CustomLayerAPI.CustomLayerType.EsriRest:
									return `${f.kind}://${f.url}`;*/
								case CustomLayerAPI.CustomLayerType.WMTS:
									return `${f.kind}://${f.capabilitiesURL}/${f.identifier}`;
								case CustomLayerAPI.CustomLayerType.EsriImage:
									return `${f.kind}://${f.url}`
							}
						}).forEach((layerSpec) => {
								this.layerSpecToNode(layerSpec)
									.catch(err => {
										const node = this.createErrorNode(layerSpec);
										this.addCustomLayerToMap(node, layerSpec);
										return node;
									})
									.then((node) => {
										if (node) {
											this.$rootScope.$broadcast('updateTreeData');
											this.$rootScope.$broadcast('onTreeChange');
										}
									}
									).catch((err) => console.warn(err));
						});
					});
				});
			}
			this.loaded = true;
		}

		private specLookup = {};

		private static coordinateRefSystemURNToEPSGCode(urn: string) : string {
			let idx = urn.indexOf('EPSG::');
			if (idx  !== -1) {
				let code = urn.substring(idx);

				return code.replace('EPSG::', 'EPSG:');
			}

			let m = urn.match('urn:ogc:def:crs:EPSG:(?:[^:]+)?:(.+)');
			if (m && m[1]) {
				return `EPSG:${m[1]}`;
			}

			return urn;
		}

		private getTreeMaxId(treeData: Item|Item[]) : number {
			return L.tree.filterNodes(treeData, (n) => n && n.layerService)
                .reduce((theMax, v) => Math.max(theMax, v.layerService.id), 0);
		}

		private layerServiceFromWMTSCapabilities(layer: any, // WMTS Capabilities Layer object
												 tileMatrixSet: any, // WMTS capabilities TileMatrixSet
												 layerType: L.util.LayerType,
												 capabilities: any) : LayerService {
			let url = null;

			if (layer.ResourceURL && layer.ResourceURL.length) {
				url = layer.ResourceURL[0].template;
			}

			if (!url) {
				let get = capabilities
					&& capabilities.OperationsMetadata
					&& capabilities.OperationsMetadata.GetTile
					&& capabilities.OperationsMetadata.GetTile
					&& capabilities.OperationsMetadata.GetTile.DCP
					&& capabilities.OperationsMetadata.GetTile.DCP.HTTP
					&& capabilities.OperationsMetadata.GetTile.DCP.HTTP.Get;

				if (get && get.length) {
					url = get[0].href;
				}
			}

			if (!url) return null;

			let legendURL: string = undefined;

			let styles = layer.Style || [];
			let defaultStyle = styles.filter((s) => s.isDefault);

			if (defaultStyle.length) {
				let maybeLegendURL = defaultStyle[0].LegendURL;
				if (maybeLegendURL && maybeLegendURL.length) {
                    legendURL = maybeLegendURL[0].href || undefined;
				}
			}

            return {
                CLASS: [],
                attributes: {
                    TileMatrixSet: tileMatrixSet.Identifier,
                    projection: getProjection(tileMatrixSet.SupportedCRS).getCode()
                },
                description: layer.Title,
                id: null,
                layerName: layer.Identifier,
                legend: false,
                longDescription: layer.Abstract,
                minScale: 0,
                maxScale: 0,
                options: {},
                params: null,
                parentId: 0,
                photoUrl: '',
                pointQuery: null,
                print: null,
                selected: true,
                url,
                sort: 1,
                'type': layerType,
				legendURL
            };
		}

		private static epsgCodeToURN(epsg: string) : string {
			return `urn:ogc:def:crs:${epsg.replace(':', '::')}`;
		}

		private pickTileMatrixSet(capabilities: any, theLayer: any) : any {
			// Filter out tile matrix sets the layer does not support
			let tileMatrixSets : any[] = capabilities.Contents.TileMatrixSet
				.filter((tms) => theLayer.TileMatrixSetLink.some((tml) => tml.TileMatrixSet == tms.Identifier));


			let mapProjection = this.mapService.map.getView().getProjection().getCode();

			let projectionURN = CustomLayers.CustomLayerService.epsgCodeToURN(mapProjection);
			// Look for a tile matrix set with the same projection as the map
			const maybeSameProjection = tileMatrixSets.find((tms) => {
				const maybeProj = getProjection(tms.SupportedCRS);
				return maybeProj && maybeProj.getCode() == mapProjection;
			});

			if (maybeSameProjection) {
				return maybeSameProjection;
			}

			let sameName = tileMatrixSets.filter((tms) => tms.Identifier == mapProjection);

			if (sameName.length) {
				return sameName[0];
			}


			// if this hasn't found a match to the current map's projection, then this will
			// use any TMS with the Google projection, and OpenLayers will re-project the tiles
			let googleCodes = ['EPSG:3857', "EPSG:900913"];
			let googleProjections = googleCodes.map(CustomLayers.CustomLayerService.epsgCodeToURN);

			let hasGoogleProjection = tileMatrixSets.filter((tms) => googleProjections.some((proj) => proj == tms.SupportedCRS));

			/*let googleProjection = CustomLayers.CustomLayerService.epsgCodeToURN('EPSG:3857');

			let hasGoogleProjection = tileMatrixSets.filter((tms) => tms.SupportedCRS == googleProjection);*/

			if (hasGoogleProjection.length) {
				return hasGoogleProjection[0];
			}

			return tileMatrixSets[0]; // by default, pick the first one and hope it works
		}

		private cachedCapabilitiesDocuments : { [url: string] : ng.IPromise<any> } = {};

		private customEsriFeatureNode(layerSpec: CustomEsriRestAPILayer): ng.IPromise<L.tree.Item> {
			const url = new URL(layerSpec.url);
			url.searchParams.set('f', 'pjson');

			const urlString = url.toString();

			const spec = this.cachedCapabilitiesDocuments[urlString];

			this.cachedCapabilitiesDocuments[urlString] = spec ||
				this.$http.get(urlString, {cache: true})
					.then(r => r.data);

			return this.cachedCapabilitiesDocuments[urlString].then((spec: FeatureLayer) => {
				const id = this.getTreeMaxId(this.mapService.treeData) + 1;

				const esriJSONFormat = new EsriJSON();

				const queryUrl = new URL(url.toString());
				queryUrl.search = '';

				const source = new VectorSource({
					attributions: spec.copyrightText,
					strategy: tile(createXYZ({
						tileSize: 512
					})),
				});

				source.setLoader(this.fetchLoader(
					(extentFromMap, resolution, projection) => {
						const extent = transformExtent(extentFromMap, projection, 'EPSG:3857');
						return `${queryUrl}/query?f=json&` +
							'returnGeometry=true&spatialRel=esriSpatialRelIntersects&geometry=' +
							encodeURIComponent('{"xmin":' + extent[0] + ',"ymin":' +
								extent[1] + ',"xmax":' + extent[2] + ',"ymax":' + extent[3] +
								',"spatialReference":{"wkid":102100}}') +
							'&geometryType=esriGeometryEnvelope&inSR=102100&outFields=*' +
							'&outSR=2193&geometryPrecision=0';
					},
					source,
					esriJSONFormat
				));

				const visible = false; // layerSpec.visible;

				const layer = new VectorLayer({
					source,
					visible,
					zIndex: Layer.zIndexForLayerType(layerSpec.type, layerSpec.order + 1)
				});

				const node: Item = {
					id,
					name: spec.name,
					isCustom: true,
					isChecked: L.tree.isChecked,
					click: L.tree.clickItem,
					checked: visible,
					layer,
					layerType: layerSpec.layerType,
					index: id,
					layerService: <any> {id},
					parent: this.mapService.getCustomLayerGroup(),
					extraState: {
						metaLayerSelector: {
							checked: !!layerSpec.included,
							expanded: true
						}
					},
					rootItems: this.mapService.treeData
				};

				this.addCustomLayerToMap(node, layerSpec);

				return node;
			});
		}

		private singleMapEsriNode(layerSpec: CustomLayerAPI.CustomEsriImageLayer, mapServer: MapService): L.tree.Item {
			const url = new URL(layerSpec.url);
			const {x, y} = mapServer.tileInfo.origin;
			const origin: Coordinate = [x, y];
			const projection = projFromSpatialReference(mapServer.spatialReference);

			const {xmin, ymin, xmax, ymax} = mapServer.fullExtent;
			const extent: Extent = [xmin, ymin, xmax, ymax];

			const visible = layerSpec.visible;

			const resolutions = mapServer.tileInfo.lods.map(lod => lod.resolution);
			const tileSize: Size = [mapServer.tileInfo.cols, mapServer.tileInfo.rows];

			const baseURL = new URL('./MapServer/tile', url);

			const source = new TileImage({
				projection,
				attributions: [mapServer.copyrightText],
				tileGrid: new TileGrid({
					origin,
					extent,
					resolutions,
					tileSize
				}),
				url: `${baseURL}/{z}/{y}/{x}`
			});

			const layer = new TileLayer({
				source,
				visible,
				zIndex: Layer.zIndexForLayerType(layerSpec.layerType, layerSpec.order + 1)
			});

			const id = this.getTreeMaxId(this.mapService.treeData) + 1;

			const node: Item = {
				id,
				name: mapServer.mapName,
				isCustom: true,
				isChecked: L.tree.isChecked,
				click: L.tree.clickItem,
				checked: visible,
				layer,
				layerType: layerSpec.layerType,
				index: id,
				layerService: <any> {id},
				parent: this.mapService.getCustomLayerGroup(),
				extraState: {
					metaLayerSelector: {
						checked: !!layerSpec.included,
						expanded: true
					}
				},
				rootItems: this.mapService.treeData
			};

			this.addCustomLayerToMap(node, layerSpec);

			return node;
		}

		private newId() {
			return this.getTreeMaxId(this.mapService.treeData) + 1;
		}

		fetchLoader(urlFn: (extent: Extent, resolution, projection) => string, source: VectorSource<Geometry>, format: FeatureFormat): FeatureLoader {
			return function (extentFromMap, resolution, projection) {
				const url = urlFn(extentFromMap, resolution, projection);
				fetch(url).then(response => {
					if (response.ok) {
						return response.json();
					} else {
						return Promise.reject();
					}
				}).then((response) => {
					const displayField = response.displayFieldName;
					const features = format.readFeatures(response) as OlFeature<Geometry>[];

					features.forEach(feature => {
						const name = feature.get(displayField);
						if (name) {
							feature.set('_title', name);
						}
					});

					source.addFeatures(features);

				}, () => {
					(<any> source).removeLoadedExtent(extentFromMap);
				});
			}
		}

		private esriFeatureNode(layerSpec: CustomEsriRestAPILayer, spec: FeatureLayer, parent: Item = undefined): L.tree.Item {
			const id = this.getTreeMaxId(this.mapService.treeData) + 1;

			const esriJSONFormat = new EsriJSON();

			const isSingleLayerURL = !!layerSpec.url.match(/\/FeatureServer\/\d+/);

			const queryUrl = new URL(layerSpec.url);
			const endsWithSlash = queryUrl.pathname[queryUrl.pathname.length - 1]  === '/';
			const join = endsWithSlash ? '' : '/';
			queryUrl.pathname = isSingleLayerURL ? queryUrl.pathname : ([queryUrl.pathname, spec.id].join(join));
			queryUrl.search = '';

			const source = new VectorSource({
				attributions: spec.copyrightText,
				strategy: tile(createXYZ({
					tileSize: 512
				})),
			});

			source.set('esriFeatureLayer', spec);

			source.setLoader(this.fetchLoader(
				(extentFromMap, resolution, projection) => {
					const [xmin, ymin, xmax, ymax] = extentFromMap;

					return `${queryUrl}/query/?f=json&` +
						'returnGeometry=true&spatialRel=esriSpatialRelIntersects&geometry=' +
						encodeURIComponent('{"xmin":' + xmin + ',"ymin":' +
							ymin + ',"xmax":' + xmax + ',"ymax":' + ymax +
							',"spatialReference":{"wkid":2193}}') +
						'&geometryType=esriGeometryEnvelope&inSR=2193&outFields=*' +
						'&outSR=2193&geometryPrecision=0'
				},
				source,
				esriJSONFormat
			));

			const layer = new VectorLayer({
				source,
				style: customLayerStyleFn,
				visible: layerSpec.visible,
				zIndex: Layer.zIndexForLayerType(layerSpec.layerType, layerSpec.order + 1)
			});

			setMapProjection(this.mapService.map.getView().getProjection());
			createStyleFunctionFromUrl(layerSpec.url).then(style => {
				layer.setStyle(style);
			}, err => {
				console.warn('unable to create style for esri layer', err);
			});

			const item: Item = {
				id,
				name: spec.name,
				isCustom: true,
				isChecked: L.tree.isChecked,
				checked: layerSpec.visible,
				click: L.tree.clickItem,
				layer,
				layerType: layerSpec.layerType,
				index: id,
				layerService: <any> {
					id,
					queryable: true
				},
				parent: parent || this.mapService.getCustomLayerGroup(),
				extraState: {
					metaLayerSelector: {
						checked: !!layerSpec.included,
						expanded: true
					}
				},
				rootItems: this.mapService.treeData
			};

			if (!parent) this.addCustomLayerToMap(item, layerSpec);

			return item;
		}

		private esriGroupNode(layer: GroupLayer, layers: Layers, legend: Legend, url: string, parent: Item): L.tree.Item {
			const layerId = layer.id;
			const subLayers = layer.subLayers.map(layerRef => layers.layers.find(l => l.id === layerRef.id));
			const id = this.newId() + layerId;
			const node: Item = {
				id,
				name: layer.name,
				longName: layer.description || '',
				isCustom: true,
				isChecked: L.tree.isChecked,
				click: L.tree.clickItem,
				index: id,
				layerService: <any> {id},
				parent,
				extraState: {
					metaLayerSelector: {
						checked: true,
						expanded: true
					}
				},
				rootItems: this.mapService.treeData
			};

			node.children = subLayers.map(layer => this.esriLayerToNode(layer, layers, legend, url, node));

			return node;
		}

		private esriRasterLayerNode(rasterLayer: GroupLayer|FeatureLayer, legend: Legend, url: string, parent: Item): L.tree.Item {
			const endsWidthSlash = url[url.length - 1] === '/';
			const relativePath = endsWidthSlash ? 'export' : 'MapServer/export';
			const exportURL = new URL(relativePath ,url);
			exportURL.search = '';

			const visible = false;

			const source = new TileArcGISRest({
				url: exportURL.toString(),
				params: {
					FORMAT: undefined,
					layers: `show:${rasterLayer.id}`
				},
				attributions: [rasterLayer.copyrightText]
			});

			const layer = new TileLayer({source, visible});

			const id = this.getTreeMaxId(this.mapService.treeData) + 1 + rasterLayer.id;

			const esriLegend = legend && legend.layers ? legend.layers.find(layer => layer.layerId === rasterLayer.id) : null;

			const node: Item = {
				id,
				name: rasterLayer.name,
				longName: rasterLayer.description || '',
				isCustom: true,
				isChecked: L.tree.isChecked,
				checked: visible,
				click: L.tree.clickItem,
				layer,
				// layerType: layerSpec.layerType,
				layerType: parent.layerType,
				index: id,
				layerService: <any> {id, esriLegend},
				parent,
				extraState: {
					metaLayerSelector: {
						checked: true,
						expanded: true
					}
				},
				rootItems: this.mapService.treeData
			};

			return node;
		}

		private esriLayerToNode(layer: GroupLayer|FeatureLayer,
								layers: Layers,
								legend: Legend,
								url: string,
								parent: L.tree.Item): L.tree.Item {
			switch (layer.type) {
				case 'Group Layer':
					return this.esriGroupNode(layer, layers, legend, url, parent);
				case 'Feature Layer':
				case 'Raster Layer':
				default:
					return this.esriRasterLayerNode(layer, legend, url, parent);
			}
		}

		private layerTreeFromEsriLayersResponse(mapServer: MapService, layersResponse: Layers, legend: Legend, url: string): L.tree.Item {
			const id = this.getTreeMaxId(this.mapService.treeData) + 1;

			const rootNode: Item = {
				id,
				name: esriServiceName(mapServer),
				isCustom: true,
				isChecked: L.tree.isChecked,
				click: L.tree.clickItem,
				index: id,
				layerService: <any> {id},
				parent: this.mapService.getCustomLayerGroup(),
				extraState: {
					metaLayerSelector: {
						checked: true,
						expanded: true
					}
				},
				rootItems: this.mapService.treeData
			};

			rootNode.children = layersResponse.layers
				.filter(layer => !layer.parentLayer)
				.map(layer => {
					return this.esriLayerToNode(layer, layersResponse, legend, url, rootNode);
				});

			return rootNode;
		}

		private layerTreeFromEsriFeatureServerResponse(featureServer: any, layerSpec: CustomEsriRestAPILayer, layersResponse: Layers): L.tree.Item {
			const id = this.getTreeMaxId(this.mapService.treeData) + 1;

			const rootNode: Item = {
				id,
				name: esriServiceName(featureServer),
				isCustom: true,
				isChecked: L.tree.isChecked,
				click: L.tree.clickItem,
				checked: layerSpec.visible,
				index: id,
				layerService: <any> {id},
				parent: this.mapService.getCustomLayerGroup(),
				extraState: {
					metaLayerSelector: {
						checked: true,
						expanded: true
					}
				},
				rootItems: this.mapService.treeData
			};

			rootNode.children = layersResponse.layers
				.filter(layer => !layer.parentLayer)
				.map(layer => {
					return this.esriFeatureNode(layerSpec, layer, rootNode);
				});

			return rootNode;
		}

		private createEsriFeatureServerNode(layerSpec: CustomLayerAPI.CustomEsriImageLayer, featureServer: any): ng.IPromise<L.tree.Item> {
			const endsWithSlash = layerSpec.url[layerSpec.url.length - 1] === '/';
			const relativePath = endsWithSlash ? 'layers' : 'FeatureServer/layers';
			const layersURL = new URL(relativePath, layerSpec.url);
			layersURL.search = "f=json";

			const layers = this.$http.get(layersURL.toString())
				.then(response => response.data);

			return layers.then((layers: Layers) => {
					const node = this.layerTreeFromEsriFeatureServerResponse(featureServer, layerSpec, layers);
					node.layerType = layerSpec.layerType;
					this.addCustomLayerToMap(node, layerSpec);
					return node;
				});
		}

		private createEsriMapServerNode(layerSpec: CustomLayerAPI.CustomEsriImageLayer, mapServer: MapService): ng.IPromise<L.tree.Item> {
			if (mapServer.singleFusedMapCache) {
				return this.$q.resolve(this.singleMapEsriNode(layerSpec, mapServer));
			}

			const endsWithSlash = layerSpec.url[layerSpec.url.length - 1] === '/';
			const relativePath = endsWithSlash ? 'layers' : 'MapServer/layers';
			const layersURL = new URL(relativePath, layerSpec.url);
			layersURL.search = "f=json";

			const legendURL = new URL(endsWithSlash ? 'legend' : 'MapServer/legend', layerSpec.url);
			legendURL.search = 'f=json';
			const legend = this.$http.get(legendURL.toString())
					.then(response => {
							const legend = response.data as any;
							if ('error' in legend || !('layers' in legend)) {
								return { layers: [] };
							}

							return legend;
						},
							err => {
							console.warn(err);
							return <Legend> { layers: [] };
						});

			const layers = this.$http.get(layersURL.toString())
				.then(response => response.data);

			return this.$q.all([layers, legend])
				.then((response: [Layers, Legend]) => {
					const node = this.layerTreeFromEsriLayersResponse(mapServer, response[0], response[1], layerSpec.url);
					node.layerType = layerSpec.layerType;
					this.addCustomLayerToMap(node, layerSpec);
					return node;
				});
		}

		private createSingleLayerNode(layerSpec: CustomLayerAPI.CustomEsriImageLayer, layer: FeatureLayer|GroupLayer): ng.IPromise<L.tree.Item> {
			const url = layerSpec.url;
			const endsWithSlash = url[url.length - 1] === '/';
			const layersURL = new URL( endsWithSlash? '../layers' : 'layers', url);
			layersURL.search = 'f=json';

			const legendURL = new URL(endsWithSlash ? '../legend' : 'legend', url);
			legendURL.search = 'f=json';

			const mapServer = new URL(endsWithSlash ? '..' : './', url);

			return this.$q.all([
					this.$http.get(layersURL.toString()).then(r => r.data),
					this.$http.get(legendURL.toString()).then(
						response => {
							const legend = response.data as any;
							if ('error' in legend || !('layers' in legend)) {
								return { layers: [] };
							}
						},
						err => {
							console.warn(err);
							return <Legend> { layers: [] };
						})
				])
				.then(([layers, legend]: [Layers, Legend]) => {
					const node = this.esriLayerToNode(layer, layers, legend, mapServer.toString(), this.mapService.getCustomLayerGroup());
					node.layerType = layerSpec.layerType;
					this.addCustomLayerToMap(node, layerSpec);

					return node;
				});
		}

		private createEsriImageServerNode(layerSpec: CustomLayerAPI.CustomEsriImageLayer, imageServer: ImageService): L.tree.Item {
			const source = new TileArcGISRest({
				url: layerSpec.url,
				params: { FORMAT: undefined },
				attributions: [imageServer.copyrightText]
			});

			const visible = layerSpec.visible;
			const layer = new TileLayer({source, visible});
			const id = this.getTreeMaxId(this.mapService.treeData) + 1;

			const node: Item = {
				id,
				name: imageServer.name,
				isCustom: true,
				isChecked: L.tree.isChecked,
				click: L.tree.clickItem,
				checked: visible,
				layer,
				layerType: layerSpec.layerType,
				index: id,
				layerService: <any> {id},
				parent: this.mapService.getCustomLayerGroup(),
				extraState: {
					metaLayerSelector: {
						checked: layerSpec.included,
						expanded: true
					}
				},
				rootItems: this.mapService.treeData
			};

			this.addCustomLayerToMap(node, layerSpec);

			return node;
		}

		private customEsriImageNode(customLayerSpec: CustomLayerAPI.CustomEsriImageLayer): ng.IPromise<L.tree.Item> {
			const url = new URL(customLayerSpec.url);
			url.searchParams.set('f', 'json');
			url.search = `f=json`;
			const urlString = url.toString();

			const spec = this.cachedCapabilitiesDocuments[urlString] = this.cachedCapabilitiesDocuments[urlString] ||
				this.$http.get(urlString, {cache: true})
					.then(r => r.data);

			console.log(`customEsriImageNode ${url}`);

			if (url.pathname.match(/\/MapServer\/\d+\/?/)) {
				return spec.then(result => this.createSingleLayerNode(customLayerSpec, result));
			} else if (url.pathname.match(/\/FeatureServer\/\d+\/?/)) {
				return spec.then(result => this.esriFeatureNode(customLayerSpec, result));
			} else if (url.pathname.match(/\/MapServer\/?/)) {
				return spec.then((result) => this.createEsriMapServerNode(customLayerSpec, result));
			} else if (url.pathname.match(/\/FeatureServer\/?/)) {
				return spec.then((result) => this.createEsriFeatureServerNode(customLayerSpec, result));
			} else if (url.pathname.match(/\/ImageServer\/?/)) {
				return spec.then(result => this.createEsriImageServerNode(customLayerSpec, result));
			} else {
				throw new Error('unsupported format/URL');
			}
		}

		private customWMTSNode(layerSpec: CustomWMTSLayer) : ng.IPromise<L.tree.Item> {
			let maybePromise = this.cachedCapabilitiesDocuments[layerSpec.capabilitiesURL];

			const capabilitiesPromise = maybePromise || this.$http.get<string>(layerSpec.capabilitiesURL, {cache: true}).then(
				(response) => {
					try {
                        const parser = new WMTSCapabilities();
                        return <any> parser.read(response.data);
					} catch (e) {
						return this.$q.reject('bad capabilities document');
                    }
				}
			);

			this.cachedCapabilitiesDocuments[layerSpec.capabilitiesURL] = capabilitiesPromise;

			return capabilitiesPromise.then((capabilities) => {
					let layers = capabilities.Contents.Layer;

					let theLayer = layers.filter((l) => {
						return l.Identifier == layerSpec.identifier
					});

					if (!theLayer.length) {
						return null;
					}

					let layerService = this.layerServiceFromWMTSCapabilities(
						theLayer[0],
						this.pickTileMatrixSet(capabilities, theLayer[0]),
						layerSpec.layerType,
						capabilities
					);

                    if (!layerService) {
                        return <any> this.$q.reject("Unable to construct supported configuration.")
                    }

					return this.$q.all([this.mapService.mapPromise, this.mapService.promise]).then(() => {
						const id = this.getTreeMaxId(this.mapService.treeData) + 1;
						layerService.id = id;
						layerService.minScale = layerSpec.minScale || 0;
						layerService.maxScale = layerSpec.maxScale || 0;

						// const getFeatureInfo = (capabilities.OperationsMetadata || {})['GetFeatureInfo'];

						// layerService['queryable'] = supportsGetFeatureInfo(capabilities);

						let layer = Layer.olTileWMTSSourceFromCapabilities(
							this.$http, layerService, capabilities, layerSpec.order, true);

						let isVisible = layerSpec.visible && layerSpec.included;
						layer.setVisible(isVisible);

						L.util.Layer.setMinMaxResolutions(layer, layerService);

						layer.set('wmtsCapabilities', capabilities);

						let node = {
							id,
							name: layerService.description,
							isCustom: true,
							isChecked: L.tree.isChecked,
							click: L.tree.clickItem,
							layer,
							checked: isVisible,
							selected: isVisible,
							layerType: layerSpec.layerType,
							layerId: id,
							layerService,
							index: id,
							parent: this.mapService.getCustomLayerGroup(),
							extraState: {
								metaLayerSelector: {
									checked: layerSpec.included,
									expanded: true
								}
							},
							rootItems: this.mapService.treeData,
                            iconUrl: layerService.legendURL
						};

						layer.set('transparencyLayer', node.layerType == 'SCIENCE');

						this.addCustomLayerToMap(node, layerSpec);

						return node;
					});
				});
		}

		private addToOlMap(item: Item) {
			if (item.layer) {
				this.mapService.map.addLayer(item.layer);
			}
			if (item.children) {
				item.children.forEach(child => this.addToOlMap(child));
			}
		}

		private addCustomLayerToMap(node: Item, layerSpec: any) {
			this.addToOlMap(node);
			const layerService = node.layerService;
			const customLayers = this.mapService.getCustomLayerGroup();
			this.specLookup[layerService.id] = layerSpec;

			const sortByFn = (a: Item, b: Item) => {
				let specA = this.specLookup[a.id];
				let specB = this.specLookup[b.id];
				return specA.order - specB.order;
			};

			customLayers.children = [node, ...customLayers.children].sort(sortByFn)
			customLayers.layerSelectorChildren = [node, ...customLayers.layerSelectorChildren].sort(sortByFn);

			const olLayers = filterNodes(customLayers, cl => cl.layer).map(cl => cl.layer).reverse();

			olLayers.forEach((l) => this.mapService.map.removeLayer(l));
			olLayers.forEach((l) => this.mapService.map.addLayer(l));

			customLayers.extraState.metaLayerSelector.checked = customLayers.children.length
				&& !customLayers.children.some(n => !n.extraState.metaLayerSelector.checked);
		}

		private createErrorNode(layerSpec: CustomLayerAPI.CustomWMTSLayer | CustomLayerAPI.CustomEsriImageLayer): Item {
			const id = this.getTreeMaxId(this.mapService.treeData) + 1;

			const name = layerSpec.kind === CustomLayerType.WMTS
				? `WMTS layer ${layerSpec.identifier} from ${layerSpec.capabilitiesURL}`
				: `Esri layer ${layerSpec.url}`;

			return {
				id,
				name,
				isChecked() { return false; },
				isCustom: true,
				isError: true,
				extraState: {
					metaLayerSelector: {
						checked: false,
						expanded: false
					}
				},
				parent: this.mapService.getCustomLayerGroup(),
				layerService: {id} as any,
				index: id,
				rootItems: this.mapService.treeData
			}
		}
	}
}